/* ============================================
   Marcas de documento — catálogo de marca blanca

   La identidad con la que sale una cotización. Hasta oct-2026 se escribía a
   mano en `cotizaciones.branding` con un UPDATE contra producción: cada
   propuesta de partner dependía de que alguien entrara a la base.

   El logo se sube a R2 y el backend deriva en la misma pasada el data URI que
   viaja al documento — Gotenberg no descarga recursos externos, así que con una
   URL el PDF saldría con el hueco donde va el logo.
   ============================================ */
import { useMemo, useRef, useState } from 'react'
import { useApi } from '@/hooks/useApi'
import { useCachedData } from '@/hooks/useCachedData'
import { usePagination } from '@/hooks/usePagination'
import { showToast } from '@/components/layout/Toast'
import EmptyState from '@/components/shared/EmptyState'
import { Badge, Button, DataTable, FormField, Modal, PageHeader, Pagination, type Column } from '@/components/ui'
import { IconEdit, IconImage, IconPlus, IconSearch, IconTrash, IconUpload } from '@/components/icons/Icons'
import type { Marca } from '@/lib/types'

const CRUMBS = [{ label: 'Configuración', to: '/configuracion' }, { label: 'Marcas de documento' }]

interface FormMarca { nombre: string; website: string; notas: string; activa: boolean; partner_id: string }
const formVacio = (): FormMarca => ({ nombre: '', website: '', notas: '', activa: true, partner_id: '' })

export default function Marcas() {
  const api = useApi()
  const [search, setSearch] = useState('')
  const [filtroEstado, setFiltroEstado] = useState('todos')

  const [modalAbierto, setModalAbierto] = useState(false)
  const [editando, setEditando] = useState<Marca | null>(null)
  const [form, setForm] = useState<FormMarca>(formVacio())
  const [archivo, setArchivo] = useState<File | null>(null)
  const [guardando, setGuardando] = useState(false)
  const inputArchivo = useRef<HTMLInputElement>(null)

  const [aEliminar, setAEliminar] = useState<Marca | null>(null)

  // En cache: volver a Marcas no vuelve a pedir el catalogo. Las altas y bajas
  // siguen llamando a `cargar()`, que ahora revalida en vez de pedir de cero.
  const { data: marcas = [], isLoading: loading, refrescar: cargar } = useCachedData(
    ['marcas'],
    () => api.get<Marca[]>('/api/marcas').catch(err => {
      showToast(err instanceof Error ? err.message : 'Error al cargar marcas', 'error')
      return [] as Marca[]
    }),
  )

  // Los partners, para poder decir de quién es cada marca. Si la lista falla,
  // el selector queda con «1Conecta» y el resto de la pantalla no se entera.
  const { data: partners = [] } = useCachedData(
    ['partners-para-marcas'],
    () => api.get<{ cliente_id: number; empresa_nombre: string }[]>('/api/auth/partners')
      .then(r => r.map(p => ({ id: p.cliente_id, empresa: p.empresa_nombre })))
      .catch(() => [] as { id: number; empresa: string }[]),
  )

  const filtradas = useMemo(() => {
    let r = marcas
    if (filtroEstado !== 'todos') r = r.filter(m => (filtroEstado === 'activa' ? m.activa : !m.activa))
    if (search) {
      const q = search.toLowerCase()
      r = r.filter(m => m.nombre.toLowerCase().includes(q) || (m.website ?? '').toLowerCase().includes(q))
    }
    return r
  }, [marcas, search, filtroEstado])

  const hayFiltros = !!search || filtroEstado !== 'todos'
  const limpiarFiltros = () => { setSearch(''); setFiltroEstado('todos') }
  const { page, setPage, totalPages, paginated } = usePagination(filtradas, 15, `${search}|${filtroEstado}`)

  const abrirNueva = () => {
    setEditando(null); setForm(formVacio()); setArchivo(null)
    if (inputArchivo.current) inputArchivo.current.value = ''
    setModalAbierto(true)
  }

  const abrirEdicion = (m: Marca) => {
    setEditando(m)
    setForm({
      nombre: m.nombre, website: m.website ?? '', notas: m.notas ?? '', activa: m.activa,
      partner_id: m.partner_id != null ? String(m.partner_id) : '',
    })
    setArchivo(null)
    if (inputArchivo.current) inputArchivo.current.value = ''
    setModalAbierto(true)
  }

  const guardar = async () => {
    if (!form.nombre.trim()) { showToast('El nombre es obligatorio', 'error'); return }
    setGuardando(true)
    try {
      const cuerpo = {
        nombre: form.nombre.trim(),
        website: form.website.trim() || null,
        notas: form.notas.trim() || null,
        activa: form.activa,
        partner_id: form.partner_id ? Number(form.partner_id) : null,
      }
      const marca = editando
        ? await api.put<Marca>(`/api/marcas/${editando.id}`, cuerpo)
        : await api.post<Marca>('/api/marcas', cuerpo)

      if (archivo) {
        const fd = new FormData()
        fd.append('file', archivo)
        await api.upload<Marca>(`/api/marcas/${marca.id}/logo`, fd)
      }

      showToast(editando ? 'Marca actualizada' : 'Marca creada', 'success')
      setModalAbierto(false); setEditando(null)
      await cargar()
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Error al guardar', 'error')
    } finally { setGuardando(false) }
  }

  const eliminar = async () => {
    if (!aEliminar) return
    try {
      const res = await api.del<{ message: string }>(`/api/marcas/${aEliminar.id}`)
      showToast(res.message, 'success')
      setAEliminar(null)
      await cargar()
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Error al eliminar', 'error')
    }
  }

  const columnas: Column<Marca>[] = [
    {
      key: 'logo', header: 'Logo', width: 150,
      render: m => m.logo_data_uri
        ? <span className="marca-logo-chip"><img src={m.logo_data_uri} alt={m.nombre} /></span>
        : <Badge variant="pausado">Sin logo</Badge>,
    },
    { key: 'nombre', header: 'Marca', render: m => <strong>{m.nombre}</strong> },
    { key: 'website', header: 'Website', render: m => m.website || '—' },
    {
      key: 'partner_id', header: 'De quién', width: 170,
      render: m => (m.partner_id != null
        ? (partners.find(p => p.id === m.partner_id)?.empresa ?? `Partner #${m.partner_id}`)
        : '1Conecta'),
    },
    {
      key: 'activa', header: 'Estado', width: 110,
      render: m => <Badge variant={m.activa ? 'activo' : 'borrador'}>{m.activa ? 'Activa' : 'Inactiva'}</Badge>,
    },
    { key: 'notas', header: 'Notas', render: m => m.notas || '—' },
    {
      key: 'acciones', header: '', align: 'right', width: 110,
      render: m => (
        <div className="marca-acciones">
          <Button size="sm" onClick={() => abrirEdicion(m)} title="Editar"><IconEdit size={14} /></Button>
          <Button size="sm" variant="danger" onClick={() => setAEliminar(m)} title="Eliminar"><IconTrash size={14} /></Button>
        </div>
      ),
    },
  ]

  if (loading) return (
    <div className="page-enter">
      <PageHeader breadcrumbs={CRUMBS} />
      <div style={{ color: 'var(--text-secondary)', textAlign: 'center', marginTop: 40 }}>
        <div className="auth-spinner" style={{ margin: '0 auto 12px' }} />Cargando...
      </div>
    </div>
  )

  return (
    <div className="page-enter page-shell">
      <PageHeader
        breadcrumbs={CRUMBS}
        meta={filtradas.length === marcas.length
          ? `${marcas.length} marca${marcas.length !== 1 ? 's' : ''}`
          : `${filtradas.length} de ${marcas.length}`}
        actions={<Button variant="primary" onClick={abrirNueva}><IconPlus size={16} /> Nueva marca</Button>}
      />

      <div className="filters-bar">
        <div className="filters-bar__search">
          <IconSearch size={14} className="filters-bar__search-icon" />
          <input className="form-input" placeholder="Buscar por nombre o website..."
            value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <select className="form-input" value={filtroEstado} onChange={e => setFiltroEstado(e.target.value)} style={{ width: 175 }}>
          <option value="todos">Activas e inactivas</option>
          <option value="activa">Solo activas</option>
          <option value="inactiva">Solo inactivas</option>
        </select>
        {hayFiltros && (
          <button className="btn btn-sm btn-secondary filters-bar__clear" onClick={limpiarFiltros}>Limpiar filtros</button>
        )}
      </div>

      {marcas.length === 0 ? (
        <EmptyState
          icon={<IconImage size={48} />}
          title="Sin marcas de documento"
          description="Una marca reemplaza la identidad de 1Conecta en la cotización: el cliente final ve al partner y nada más."
          actionLabel="+ Nueva marca"
          onAction={abrirNueva}
        />
      ) : (
        <>
          <div className="page-shell__table">
            <DataTable
              columns={columnas}
              rows={paginated}
              rowKey={m => m.id}
              empty="Ninguna marca coincide con el filtro."
            />
          </div>
          <Pagination page={page} totalPages={totalPages} total={filtradas.length} pageSize={15} onPage={setPage} noun="marcas" />
        </>
      )}

      <Modal
        open={modalAbierto}
        onClose={() => { setModalAbierto(false); setEditando(null) }}
        title={editando ? `Editar ${editando.nombre}` : 'Nueva marca'}
        footer={
          <>
            <Button onClick={() => { setModalAbierto(false); setEditando(null) }} disabled={guardando}>Cancelar</Button>
            <Button variant="primary" onClick={guardar} disabled={guardando}>
              {guardando ? 'Guardando...' : 'Guardar'}
            </Button>
          </>
        }
      >
        <FormField label="Nombre">
          <input className="form-input" value={form.nombre}
            onChange={e => setForm(f => ({ ...f, nombre: e.target.value }))} placeholder="Ej: Brainstorming" />
        </FormField>
        <FormField label="Website" hint="Sale en el pie de cada hoja del documento.">
          <input className="form-input" value={form.website}
            onChange={e => setForm(f => ({ ...f, website: e.target.value }))} placeholder="www.brainstorming.la" />
        </FormField>
        <FormField
          label="Logo"
          hint="PNG, JPG, WebP o SVG, hasta 5 MB. Se guarda en R2 y se incrusta en el documento (el PDF no puede descargar imágenes de internet)."
        >
          <input ref={inputArchivo} className="form-input" type="file"
            accept="image/png,image/jpeg,image/webp,image/svg+xml"
            onChange={e => setArchivo(e.target.files?.[0] ?? null)} />
        </FormField>
        {(archivo || editando?.logo_data_uri) && (
          <div className="marca-logo-preview">
            <span className="marca-logo-preview__label">
              {archivo ? <><IconUpload size={13} /> Reemplaza el logo actual</> : 'Logo actual'}
            </span>
            <span className="marca-logo-chip">
              <img src={archivo ? URL.createObjectURL(archivo) : editando!.logo_data_uri!} alt={form.nombre} />
            </span>
          </div>
        )}
        <FormField label="Notas" hint="Interno: no sale en el documento.">
          <textarea className="form-textarea" rows={2} value={form.notas}
            onChange={e => setForm(f => ({ ...f, notas: e.target.value }))} />
        </FormField>
        <FormField
          label="De quién es"
          hint="Una marca de un partner solo la ve él al cotizar. Nosotros las vemos todas, porque también armamos documentos con su marca. Cambiar el dueño no toca las cotizaciones ya emitidas: el documento lleva su propio snapshot."
        >
          <select
            className="form-input"
            value={form.partner_id}
            onChange={e => setForm(f => ({ ...f, partner_id: e.target.value }))}
          >
            <option value="">1Conecta</option>
            {partners.map(p => <option key={p.id} value={String(p.id)}>{p.empresa}</option>)}
          </select>
        </FormField>
        <FormField hint="Una marca inactiva no se ofrece al cotizar, pero las cotizaciones ya emitidas la conservan.">
          <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: 8, margin: 0 }}>
            <input type="checkbox" checked={form.activa}
              onChange={e => setForm(f => ({ ...f, activa: e.target.checked }))} /> Activa
          </label>
        </FormField>
      </Modal>

      <Modal
        open={!!aEliminar}
        onClose={() => setAEliminar(null)}
        title="Eliminar marca"
        footer={
          <>
            <Button onClick={() => setAEliminar(null)}>Cancelar</Button>
            <Button variant="danger" onClick={eliminar}>Eliminar</Button>
          </>
        }
      >
        <p>¿Eliminar <strong>{aEliminar?.nombre}</strong>?</p>
        <p style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
          Si tiene cotizaciones emitidas se desactiva en vez de borrarse: el documento lleva su
          propia copia de la marca, pero el editor necesita reconocerla.
        </p>
      </Modal>
    </div>
  )
}
